import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { Hint } from '@synoptic/ui-kit/hint/hint.tsx';
import { LinkButton } from '@synoptic/ui-kit/link-button/link-button.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
  ModalTrigger,
} from '@synoptic/ui-kit/modal/modal.tsx';
import {
  MultiTag,
  MultiTagItem,
} from '@synoptic/ui-kit/multi-tag/multi-tag.tsx';
import { Wrap } from '@synoptic/ui-kit/wrap.js';
import { ReactNode, useState } from 'react';
import { useParams } from 'react-router';
import { CustomTargetsDataFragmentFragment } from '@/__generated__/graphql.ts';
import { StreamSubscription } from '@/__generated__/schema-types.ts';
import {
  actionsContainer,
  modalDescription,
  targetsBottomTip,
  targetTag,
} from '@/components/streams/custom-stream/targets-modal.css.ts';
import { useSetCustomTargets } from '@/components/streams/custom-stream/use-set-custom-targets.ts';
import { useUpdateStreamSubscription } from '@/components/streams/custom-stream/use-update-stream-subscription.ts';
import { PriceStepperSection } from '@/components/streams/payment-options/price-stepper-section.tsx';
import {
  getInitialTargetsCount,
  getNextValidTargetsCount,
} from '../payment-options/price-stepper-utils';

type SubscriptionPricing = Pick<
  StreamSubscription,
  'price' | 'interval' | 'intervalCount' | 'customTargets' | 'cancelAt'
>;

type View = 'targets' | 'updateQuantity';

export const TargetsModal = ({
  trigger,
  streamId,
  maxTargets,
  initialTargets,
  instruction,
  targetValidationRegex,
  refetchStreamPosts,
  customTargetsData,
  subscription,
  open: controlledOpen,
  onOpenChange: onControlledOpenChange,
}: {
  trigger?: ReactNode;
  streamId?: string;
  maxTargets: number;
  initialTargets?: string[];
  instruction?: string;
  targetValidationRegex?: string | null;
  refetchStreamPosts?: boolean;
  customTargetsData?: CustomTargetsDataFragmentFragment | null;
  subscription?: SubscriptionPricing | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const routeStreamId = useParams().id as string;
  const id = streamId ?? routeStreamId;
  const [internalOpen, setInternalOpen] = useState(false);
  const [view, setView] = useState<View>('targets');

  const open = controlledOpen ?? internalOpen;
  const onOpenChange = (next: boolean) => {
    setInternalOpen(next);
    onControlledOpenChange?.(next);
    if (!next) setView('targets');
  };

  const canAddMoreTargets = !!customTargetsData && !!subscription;
  const isAtMaxLimit =
    canAddMoreTargets &&
    getNextValidTargetsCount(
      subscription.customTargets ?? 0,
      customTargetsData,
    ) > customTargetsData.maxTargets;
  const isSubscriptionCancelled = !!subscription?.cancelAt;

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange}>
      {trigger ? <ModalTrigger asChild>{trigger}</ModalTrigger> : null}
      <ModalContent>
        {view === 'targets' ? (
          <EditTargetsView
            streamId={id}
            maxTargets={maxTargets}
            initialTargets={initialTargets}
            instruction={instruction}
            targetValidationRegex={targetValidationRegex}
            refetchStreamPosts={refetchStreamPosts}
            onClose={() => onOpenChange(false)}
            onAddMoreTargets={
              canAddMoreTargets ? () => setView('updateQuantity') : undefined
            }
            isAtMaxLimit={isAtMaxLimit}
            isSubscriptionCancelled={isSubscriptionCancelled}
          />
        ) : (
          <UpdateQuantityView
            streamId={id}
            customTargetsData={customTargetsData!}
            subscription={subscription!}
            onBack={() => setView('targets')}
            onSuccess={() => onOpenChange(false)}
          />
        )}
      </ModalContent>
    </ModalRoot>
  );
};

const EditTargetsView = ({
  streamId,
  maxTargets,
  initialTargets,
  instruction,
  targetValidationRegex,
  refetchStreamPosts,
  onClose,
  onAddMoreTargets,
  isAtMaxLimit,
  isSubscriptionCancelled,
}: {
  streamId: string;
  maxTargets: number;
  initialTargets?: string[];
  instruction?: string;
  targetValidationRegex?: string | null;
  refetchStreamPosts?: boolean;
  onClose: () => void;
  onAddMoreTargets?: () => void;
  isAtMaxLimit?: boolean;
  isSubscriptionCancelled?: boolean;
}) => {
  const [targets, setTargets] = useState<string[]>(initialTargets || []);

  const [setCustomTargets, { loading }] = useSetCustomTargets(
    streamId,
    refetchStreamPosts,
  );

  const onSubmit = async () => {
    await setCustomTargets(targets);
    onClose();
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Submit your targets</ModalTitle>
      </ModalHeader>
      <p className={modalDescription}>
        Add the targets you want this stream to monitor. Follow the author’s
        instruction and separate targets with a whitespace or comma in between —
        we’ll sort it out.
      </p>
      {instruction ? (
        <p className={modalDescription}>
          <strong>Author&apos;s instruction: </strong>
          {instruction}
        </p>
      ) : null}
      {targetValidationRegex ? (
        <p className={modalDescription}>
          <strong>Regex: </strong>
          {targetValidationRegex}
        </p>
      ) : null}

      <MultiTag
        value={targets}
        onChange={setTargets}
        label={'Select targets you want to monitor:'}
        placeholder={'Type or paste targets here...'}
        maxItems={maxTargets}
        bottomTip={
          <div className={targetsBottomTip}>
            <span>
              {targets.length} / {maxTargets} target
              {targets.length !== 1 ? 's' : ''} added
            </span>{' '}
            {onAddMoreTargets ? (
              <Wrap
                with={Hint}
                if={isAtMaxLimit || isSubscriptionCancelled}
                description={
                  isSubscriptionCancelled
                    ? 'Your subscription is cancelled. Renew it to add more targets.'
                    : 'You already have the maximum targets limit allowed by the author.'
                }
              >
                <LinkButton
                  size="medium"
                  onClick={onAddMoreTargets}
                  disabled={isAtMaxLimit || isSubscriptionCancelled}
                >
                  Add more targets
                </LinkButton>
              </Wrap>
            ) : null}
          </div>
        }
        regex={targetValidationRegex ?? undefined}
        TagItem={({ children, onRemove }) => (
          <MultiTagItem onRemove={onRemove} className={targetTag}>
            {children}
          </MultiTagItem>
        )}
      />

      <div className={actionsContainer}>
        <Button variant={'secondary'} onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={targets.length <= 0 || targets.length > maxTargets}
          loading={loading}
        >
          Submit targets
        </Button>
      </div>
    </>
  );
};

const UpdateQuantityView = ({
  streamId,
  customTargetsData,
  subscription,
  onBack,
  onSuccess,
}: {
  streamId: string;
  customTargetsData: CustomTargetsDataFragmentFragment;
  subscription: SubscriptionPricing;
  onBack: () => void;
  onSuccess: () => void;
}) => {
  const initialTargetsCount =
    subscription.customTargets ?? getInitialTargetsCount(customTargetsData);
  const nextValidTargetsCount = getNextValidTargetsCount(
    initialTargetsCount,
    customTargetsData,
  );

  const [targetsCount, setTargetsCount] = useState(initialTargetsCount);

  const [updateSubscription, { loading }] =
    useUpdateStreamSubscription(streamId);

  const stepperData = {
    ...customTargetsData,
    minTargets: nextValidTargetsCount,
  };

  const handleSetTargetsCount = (next: number) => {
    if (next > targetsCount && next < nextValidTargetsCount) {
      setTargetsCount(nextValidTargetsCount);
      return;
    }
    setTargetsCount(next);
  };

  const totalSteps = targetsCount / customTargetsData.stepSize;
  const isBelowMinValid = targetsCount < nextValidTargetsCount;

  const onSubmit = async () => {
    await updateSubscription(totalSteps);
    onSuccess();
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle>Add more targets</ModalTitle>
      </ModalHeader>
      <p className={modalDescription}>
        Increase how many targets your subscription monitors. Your billing will
        be updated accordingly.
      </p>

      <PriceStepperSection
        customTargets={stepperData}
        selectedPrice={{
          amount: subscription.price,
          interval: subscription.interval,
          intervalCount: subscription.intervalCount,
        }}
        targetsCount={targetsCount}
        setTargetsCount={handleSetTargetsCount}
      />

      <div className={actionsContainer}>
        <Button variant={'secondary'} onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button onClick={onSubmit} disabled={isBelowMinValid} loading={loading}>
          Update
        </Button>
      </div>
    </>
  );
};
